/**
 * DocsManagerPage — full-admin CRUD surface for the internal Admin Docs
 * knowledge base. Mounted at /admin/tools/docs-manager.
 *
 * Key behaviours:
 * - Fetches ALL sections (active + inactive) via GET /admin/docs/sections/all
 * - Per-article visibility: "All sub-admins" (empty caps array) or
 *   "Specific capabilities" (non-empty caps, ANY-of match on read).
 * - Save is disabled when "Specific" is chosen but 0 capabilities are ticked.
 * - Category management reuses the shared /admin/docs/categories endpoints.
 */
import React, { useState, useEffect, useCallback, Suspense, lazy } from 'react';
import axios from 'axios';
import {
  DndContext, closestCenter, PointerSensor,
  useSensor, useSensors,
} from '@dnd-kit/core';
import {
  arrayMove, SortableContext,
  verticalListSortingStrategy, useSortable,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import Layout from '../../components/layout/Layout';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Badge } from '../../components/ui/badge';
import { Switch } from '../../components/ui/switch';
import { Checkbox } from '../../components/ui/checkbox';
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from '../../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../components/ui/dialog';
import {
  ArrowLeft, Plus, Edit, Trash2, Eye, EyeOff, Save, Video,
  LifeBuoy, FolderCog, Lock, Unlock, AlertCircle, GripVertical,
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { CAP_GROUPS, CAP_LABEL } from '../../utils/capabilityCatalog';

const ReactQuill = lazy(() => import('react-quill-new'));

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const QUILL_MODULES = {
  toolbar: [
    [{ header: [1, 2, 3, false] }],
    ['bold', 'italic', 'underline', 'strike'],
    [{ list: 'ordered' }, { list: 'bullet' }],
    [{ indent: '-1' }, { indent: '+1' }],
    ['link'],
    ['clean'],
  ],
};
const QUILL_FORMATS = [
  'header', 'bold', 'italic', 'underline', 'strike',
  'list', 'bullet', 'indent', 'link',
];

const EMPTY_FORM = {
  title: '',
  content: '',
  video_embeds: [],
  category_id: '',
  required_capabilities: [],
  admin_only: false,
  is_active: true,
};

// ─── Capability multi-select picker ────────────────────────────────────────
const CapabilityPicker = ({ selected, onChange }) => {
  const toggle = (key) => {
    onChange(
      selected.includes(key)
        ? selected.filter((k) => k !== key)
        : [...selected, key],
    );
  };
  return (
    <div className="space-y-3 border rounded-md p-3 bg-slate-50/50 max-h-72 overflow-y-auto">
      {CAP_GROUPS.map((group) => (
        <div key={group.module}>
          <p className="text-[10px] font-semibold uppercase tracking-wide text-slate-500 mb-1.5">
            {group.module}
          </p>
          <div className="space-y-1.5">
            {group.caps.map((cap) => {
              const checked = selected.includes(cap.key);
              return (
                <label
                  key={cap.key}
                  className="flex items-start gap-2 text-sm cursor-pointer select-none"
                  data-testid={`cap-checkbox-${cap.key}`}
                >
                  <Checkbox
                    checked={checked}
                    onCheckedChange={() => toggle(cap.key)}
                    className="mt-0.5 flex-shrink-0"
                  />
                  <div className="min-w-0">
                    <span className="font-medium text-slate-800">{cap.title}</span>
                    <span className="text-slate-500 ml-1.5 text-xs">{cap.desc}</span>
                  </div>
                </label>
              );
            })}
          </div>
        </div>
      ))}
    </div>
  );
};

// ─── Article row in the list ────────────────────────────────────────────────
// ── Sortable wrappers (drag-and-drop reorder) ─────────────────────────────────

const SortableArticleRow = ({ id, children }) => {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } =
    useSortable({ id });
  return (
    <div
      ref={setNodeRef}
      style={{ transform: CSS.Transform.toString(transform), transition, opacity: isDragging ? 0.5 : 1, zIndex: isDragging ? 50 : 'auto' }}
      className="flex items-stretch gap-1"
    >
      <button
        type="button"
        {...attributes}
        {...listeners}
        className="flex items-center px-1 text-slate-300 hover:text-slate-500 cursor-grab active:cursor-grabbing touch-none"
        title="Drag to reorder"
        onClick={(e) => e.stopPropagation()}
        data-testid={`article-drag-handle-${id}`}
      >
        <GripVertical className="w-4 h-4" />
      </button>
      <div className="flex-1 min-w-0">{children}</div>
    </div>
  );
};

const SortableCategoryRow = ({ id, children }) => {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } =
    useSortable({ id });
  return (
    <div
      ref={setNodeRef}
      style={{ transform: CSS.Transform.toString(transform), transition, opacity: isDragging ? 0.5 : 1, zIndex: isDragging ? 50 : 'auto' }}
      className="flex items-center gap-1"
    >
      <button
        type="button"
        {...attributes}
        {...listeners}
        className="flex items-center px-1 py-2 text-slate-300 hover:text-slate-500 cursor-grab active:cursor-grabbing touch-none flex-shrink-0"
        title="Drag to reorder"
        onClick={(e) => e.stopPropagation()}
        data-testid={`category-drag-handle-${id}`}
      >
        <GripVertical className="w-4 h-4" />
      </button>
      <div className="flex-1 min-w-0">{children}</div>
    </div>
  );
};

// ─────────────────────────────────────────────────────────────────────────────

const ArticleRow = ({ article, categories, onEdit, onToggleActive, onDelete }) => {
  const cat = categories.find((c) => c.id === article.category_id);
  const caps = article.required_capabilities || [];
  return (
    <div
      className={`flex items-start justify-between p-3 border rounded-lg gap-3 ${!article.is_active ? 'bg-slate-50 opacity-60' : ''}`}
      data-testid={`docs-manager-article-${article.id}`}
    >
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 flex-wrap">
          <span className={`font-medium text-slate-900 ${!article.is_active ? 'line-through' : ''}`}>
            {article.title}
          </span>
          {cat && (
            <Badge variant="outline" className="text-xs">{cat.name}</Badge>
          )}
          {!article.is_active && (
            <Badge variant="secondary" className="text-xs">Draft</Badge>
          )}
        </div>
        {caps.length > 0 ? (
          <div className="mt-1 flex flex-wrap items-center gap-1 text-[11px] text-amber-700">
            <Lock className="w-3 h-3" />
            <span className="font-medium">Specific caps:</span>
            {caps.map((c) => (
              <span key={c} className="px-1.5 py-0.5 bg-amber-50 border border-amber-200 rounded">
                {CAP_LABEL[c] || c}
              </span>
            ))}
          </div>
        ) : article.admin_only ? (
          <div className="mt-1 flex items-center gap-1 text-[11px] text-red-700">
            <Lock className="w-3 h-3" />
            <span className="font-medium">Full admins only</span>
          </div>
        ) : (
          <div className="mt-1 flex items-center gap-1 text-[11px] text-emerald-700">
            <Unlock className="w-3 h-3" />
            <span>All sub-admins</span>
          </div>
        )}
      </div>
      <div className="flex items-center gap-1 flex-shrink-0">
        <Button
          variant="ghost"
          size="sm"
          onClick={() => onToggleActive(article)}
          title={article.is_active ? 'Set as Draft' : 'Publish'}
          data-testid={`docs-toggle-active-${article.id}`}
        >
          {article.is_active ? <Eye className="w-4 h-4" /> : <EyeOff className="w-4 h-4" />}
        </Button>
        <Button
          variant="ghost"
          size="sm"
          onClick={() => onEdit(article)}
          data-testid={`docs-edit-btn-${article.id}`}
        >
          <Edit className="w-4 h-4" />
        </Button>
        <Button
          variant="ghost"
          size="sm"
          onClick={() => onDelete(article)}
          data-testid={`docs-delete-btn-${article.id}`}
        >
          <Trash2 className="w-4 h-4 text-red-500" />
        </Button>
      </div>
    </div>
  );
};

// ─── Main page component ─────────────────────────────────────────────────────
const DocsManagerPage = () => {
  const navigate = useNavigate();
  const [sections, setSections] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);

  // Article dialog state
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingArticle, setEditingArticle] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);
  // 'all' | 'specific' — controls whether required_capabilities are set
  const [visibilityMode, setVisibilityMode] = useState('all');
  const [saving, setSaving] = useState(false);

  // Category management dialog
  const [catDialogOpen, setCatDialogOpen] = useState(false);
  const [catNew, setCatNew] = useState('');
  const [catEditingId, setCatEditingId] = useState(null);
  const [catEditName, setCatEditName] = useState('');
  const [savingCat, setSavingCat] = useState(false);

  // DnD sensors (pointer only — no keyboard sensor to avoid accidental reorders)
  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 6 } }));

  // ── Data fetchers ─────────────────────────────────────────────────────────
  const fetchAll = useCallback(async () => {
    try {
      setLoading(true);
      const [secRes, catRes] = await Promise.all([
        axios.get(`${API}/admin/docs/sections/all`),
        axios.get(`${API}/admin/docs/categories`),
      ]);
      setSections(secRes.data || []);
      setCategories(catRes.data || []);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to load docs');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  // ── Drag-and-drop reorder handlers ───────────────────────────────────────
  const handleArticleDragEnd = useCallback(async (event, categoryId) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    setSections((prev) => {
      const catArticles = prev.filter((s) => (s.category_id || '__uncat__') === categoryId);
      const others = prev.filter((s) => (s.category_id || '__uncat__') !== categoryId);
      const oldIdx = catArticles.findIndex((s) => s.id === active.id);
      const newIdx = catArticles.findIndex((s) => s.id === over.id);
      const reordered = arrayMove(catArticles, oldIdx, newIdx);
      const next = [...others, ...reordered];
      // Fire-and-forget persist
      axios.post(`${API}/admin/docs/sections/reorder`, {
        section_ids: reordered.map((s) => s.id),
      }).catch(() => toast.error('Failed to save article order'));
      return next;
    });
  }, []);

  const handleCategoryDragEnd = useCallback(async (event) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    setCategories((prev) => {
      const oldIdx = prev.findIndex((c) => c.id === active.id);
      const newIdx = prev.findIndex((c) => c.id === over.id);
      const reordered = arrayMove(prev, oldIdx, newIdx);
      axios.post(`${API}/admin/docs/categories/reorder`, {
        category_ids: reordered.map((c) => c.id),
      }).catch(() => toast.error('Failed to save category order'));
      return reordered;
    });
  }, []);

  // ── Article CRUD ─────────────────────────────────────────────────────────
  const openCreate = () => {
    setEditingArticle(null);
    setForm(EMPTY_FORM);
    setVisibilityMode('all');
    setDialogOpen(true);
  };

  const openEdit = (article) => {
    setEditingArticle(article);
    setForm({
      title: article.title,
      content: article.content || '',
      video_embeds: article.video_embeds || [],
      category_id: article.category_id || '',
      required_capabilities: article.required_capabilities || [],
      admin_only: article.admin_only || false,
      is_active: article.is_active ?? true,
    });
    const mode = article.admin_only
      ? 'admin_only'
      : (article.required_capabilities || []).length > 0
        ? 'specific'
        : 'all';
    setVisibilityMode(mode);
    setDialogOpen(true);
  };

  const saveArticle = async () => {
    if (!form.title.trim()) { toast.error('Title is required'); return; }
    const isAdminOnly = visibilityMode === 'admin_only';
    const caps = visibilityMode === 'specific' ? form.required_capabilities : [];
    if (visibilityMode === 'specific' && caps.length === 0) {
      toast.error('Select at least one capability, or switch visibility mode');
      return;
    }
    const payload = { ...form, required_capabilities: caps, admin_only: isAdminOnly };
    try {
      setSaving(true);
      if (editingArticle) {
        await axios.put(`${API}/admin/docs/sections/${editingArticle.id}`, payload);
        toast.success('Article updated');
      } else {
        await axios.post(`${API}/admin/docs/sections`, payload);
        toast.success('Article created');
      }
      setDialogOpen(false);
      fetchAll();
    } catch (err) {
      const detail = err.response?.data?.detail;
      toast.error(typeof detail === 'string' ? detail : 'Failed to save article');
    } finally {
      setSaving(false);
    }
  };

  const toggleActive = async (article) => {
    try {
      await axios.put(`${API}/admin/docs/sections/${article.id}`, { is_active: !article.is_active });
      toast.success(article.is_active ? 'Article set to draft' : 'Article published');
      fetchAll();
    } catch {
      toast.error('Failed to update article');
    }
  };

  const deleteArticle = async (article) => {
    if (!window.confirm(`Delete "${article.title}"? This cannot be undone.`)) return;
    try {
      await axios.delete(`${API}/admin/docs/sections/${article.id}`);
      toast.success('Article deleted');
      fetchAll();
    } catch {
      toast.error('Failed to delete article');
    }
  };

  // ── Category CRUD ─────────────────────────────────────────────────────────
  const addCategory = async () => {
    const name = catNew.trim();
    if (!name) return;
    try {
      setSavingCat(true);
      await axios.post(`${API}/admin/docs/categories`, { name });
      setCatNew('');
      toast.success('Category added');
      const { data } = await axios.get(`${API}/admin/docs/categories`);
      setCategories(data || []);
    } catch (e) {
      const detail = e?.response?.data?.detail;
      toast.error(typeof detail === 'string' ? detail : 'Failed to add category');
    } finally {
      setSavingCat(false);
    }
  };

  const saveCategoryEdit = async (categoryId) => {
    const name = catEditName.trim();
    if (!name) return;
    try {
      setSavingCat(true);
      await axios.put(`${API}/admin/docs/categories/${categoryId}`, { name });
      setCatEditingId(null);
      setCatEditName('');
      toast.success('Category updated');
      const { data } = await axios.get(`${API}/admin/docs/categories`);
      setCategories(data || []);
    } catch (e) {
      const detail = e?.response?.data?.detail;
      toast.error(typeof detail === 'string' ? detail : 'Failed to update category');
    } finally {
      setSavingCat(false);
    }
  };

  const deleteCategory = async (cat) => {
    if (!window.confirm(`Delete category "${cat.name}"? Articles in this category become uncategorised.`)) return;
    try {
      await axios.delete(`${API}/admin/docs/categories/${cat.id}`);
      toast.success('Category deleted');
      const [{ data: cats }, { data: secs }] = await Promise.all([
        axios.get(`${API}/admin/docs/categories`),
        axios.get(`${API}/admin/docs/sections/all`),
      ]);
      setCategories(cats || []);
      setSections(secs || []);
    } catch (e) {
      const detail = e?.response?.data?.detail;
      toast.error(typeof detail === 'string' ? detail : 'Failed to delete category');
    }
  };

  // ── Save gating ───────────────────────────────────────────────────────────
  const saveDisabled =
    saving ||
    !form.title.trim() ||
    (visibilityMode === 'specific' && form.required_capabilities.length === 0);

  // ── Group articles by category for the list view ──────────────────────────
  const grouped = (() => {
    const byCat = new Map();
    for (const s of sections) {
      const cid = s.category_id || '__uncat__';
      if (!byCat.has(cid)) byCat.set(cid, []);
      byCat.get(cid).push(s);
    }
    const order = [
      ...categories.map((c) => ({ id: c.id, name: c.name })),
      { id: '__uncat__', name: 'Uncategorised' },
    ];
    return order
      .map((c) => ({ ...c, articles: byCat.get(c.id) || [] }))
      .filter((c) => c.articles.length > 0);
  })();

  // ─────────────────────────────────────────────────────────────────────────
  return (
    <Layout>
      <div className="page-transition">
        <div className="flex items-center gap-4 mb-6">
          <Button variant="ghost" onClick={() => navigate('/admin/tools')} className="text-slate-600">
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Tools
          </Button>
        </div>

        <Card className="mb-6 border-l-4 border-l-emerald-500 shadow-sm">
          <CardHeader>
            <div className="flex items-start justify-between flex-wrap gap-3">
              <div className="flex items-center gap-3">
                <div className="p-2.5 bg-emerald-100 rounded-lg">
                  <LifeBuoy className="w-6 h-6 text-emerald-700" />
                </div>
                <div>
                  <CardTitle className="text-2xl text-slate-900">Docs Manager</CardTitle>
                  <CardDescription className="mt-0.5">
                    Author internal how-to guides for admins and sub-admins. Control visibility per article via capability gating.
                  </CardDescription>
                </div>
              </div>
              <div className="flex gap-2">
                <Button
                  variant="outline"
                  onClick={() => setCatDialogOpen(true)}
                  data-testid="docs-manager-manage-categories-btn"
                >
                  <FolderCog className="w-4 h-4 mr-2" />
                  Manage Categories
                </Button>
                <Button
                  onClick={openCreate}
                  data-testid="docs-manager-add-article-btn"
                >
                  <Plus className="w-4 h-4 mr-2" />
                  New Article
                </Button>
              </div>
            </div>
          </CardHeader>
        </Card>

        {loading ? (
          <div className="py-12 text-center text-slate-500">Loading…</div>
        ) : sections.length === 0 ? (
          <Card>
            <CardContent className="p-12 text-center text-slate-500">
              <LifeBuoy className="w-10 h-10 mx-auto mb-3 text-slate-300" />
              <div className="text-sm font-medium text-slate-700 mb-1">No articles yet</div>
              <div className="text-xs mb-4">Create your first internal doc using the button above.</div>
              <Button size="sm" onClick={openCreate}>
                <Plus className="w-3.5 h-3.5 mr-1" /> New Article
              </Button>
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-6" data-testid="docs-manager-article-list">
            {grouped.map((cat) => (
              <section key={cat.id}>
                <h2 className="text-xs font-semibold uppercase tracking-wide text-slate-500 mb-2">
                  {cat.name}
                  <span className="ml-1.5 font-normal normal-case text-slate-400">
                    · {cat.articles.length}
                  </span>
                </h2>
                <DndContext
                  sensors={sensors}
                  collisionDetection={closestCenter}
                  onDragEnd={(e) => handleArticleDragEnd(e, cat.id)}
                >
                  <SortableContext
                    items={cat.articles.map((a) => a.id)}
                    strategy={verticalListSortingStrategy}
                  >
                    <div className="space-y-2">
                      {cat.articles.map((article) => (
                        <SortableArticleRow key={article.id} id={article.id}>
                          <ArticleRow
                            article={article}
                            categories={categories}
                            onEdit={openEdit}
                            onToggleActive={toggleActive}
                            onDelete={deleteArticle}
                          />
                        </SortableArticleRow>
                      ))}
                    </div>
                  </SortableContext>
                </DndContext>
              </section>
            ))}
          </div>
        )}

        {/* ── Article editor dialog ──────────────────────────────────────── */}
        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="docs-article-dialog">
            <DialogHeader>
              <DialogTitle>
                {editingArticle ? 'Edit Article' : 'New Article'}
              </DialogTitle>
            </DialogHeader>

            <div className="space-y-5">
              {/* Title */}
              <div>
                <Label htmlFor="docs-title">Title *</Label>
                <Input
                  id="docs-title"
                  value={form.title}
                  onChange={(e) => setForm({ ...form, title: e.target.value })}
                  placeholder="e.g., How to approve a question"
                  className="mt-1"
                  data-testid="docs-title-input"
                />
              </div>

              {/* Category */}
              <div>
                <Label htmlFor="docs-category">Category</Label>
                <Select
                  value={form.category_id || 'none'}
                  onValueChange={(v) => setForm({ ...form, category_id: v === 'none' ? '' : v })}
                >
                  <SelectTrigger id="docs-category" className="mt-1" data-testid="docs-category-select">
                    <SelectValue placeholder="Uncategorised" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">Uncategorised</SelectItem>
                    {categories.map((c) => (
                      <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Content (rich text) */}
              <div>
                <Label>Content</Label>
                <div className="border rounded-lg mt-1">
                  <Suspense fallback={
                    <div className="min-h-[260px] bg-slate-50 rounded-lg flex items-center justify-center text-slate-400">
                      <div className="text-center">
                        <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-emerald-600 mx-auto mb-2" />
                        <span className="text-sm">Loading editor…</span>
                      </div>
                    </div>
                  }>
                    <ReactQuill
                      theme="snow"
                      value={form.content}
                      onChange={(v) => setForm((f) => ({ ...f, content: v }))}
                      modules={QUILL_MODULES}
                      formats={QUILL_FORMATS}
                      className="min-h-[260px]"
                      data-testid="docs-content-editor"
                    />
                  </Suspense>
                </div>
              </div>

              {/* Video embeds */}
              <div data-testid="docs-video-embeds">
                <div className="flex items-center justify-between mb-2">
                  <Label className="flex items-center gap-2">
                    <Video className="w-4 h-4 text-emerald-600" />
                    Videos (optional)
                  </Label>
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() =>
                      setForm((f) => ({
                        ...f,
                        video_embeds: [...(f.video_embeds || []), { url: '', title: '' }],
                      }))
                    }
                    data-testid="docs-add-video-btn"
                  >
                    <Plus className="w-3.5 h-3.5 mr-1" /> Add video
                  </Button>
                </div>
                <p className="text-xs text-slate-500 mb-2">
                  Paste a YouTube or Dadan link (dadan.io or video.helmtorq.com), share URL, or embed HTML — we'll normalise it.
                </p>
                {(form.video_embeds || []).length === 0 ? (
                  <p className="text-xs text-slate-400 italic">No videos added.</p>
                ) : (
                  <div className="space-y-2">
                    {(form.video_embeds || []).map((v, idx) => (
                      <div
                        key={idx}
                        className="border rounded-md p-2 bg-slate-50 flex flex-col sm:flex-row gap-2"
                        data-testid={`docs-video-row-${idx}`}
                      >
                        <Input
                          type="text"
                          value={v.title || ''}
                          placeholder="Title (optional)"
                          className="sm:w-44"
                          onChange={(e) =>
                            setForm((f) => {
                              const arr = [...(f.video_embeds || [])];
                              arr[idx] = { ...arr[idx], title: e.target.value };
                              return { ...f, video_embeds: arr };
                            })
                          }
                          data-testid={`docs-video-title-${idx}`}
                        />
                        <Input
                          type="text"
                          value={v.url || ''}
                          placeholder="YouTube or Dadan URL (dadan.io / video.helmtorq.com)"
                          className="flex-1 font-mono text-xs"
                          onChange={(e) =>
                            setForm((f) => {
                              const arr = [...(f.video_embeds || [])];
                              arr[idx] = { ...arr[idx], url: e.target.value };
                              return { ...f, video_embeds: arr };
                            })
                          }
                          data-testid={`docs-video-url-${idx}`}
                        />
                        <Button
                          type="button"
                          variant="ghost"
                          size="icon"
                          onClick={() =>
                            setForm((f) => ({
                              ...f,
                              video_embeds: (f.video_embeds || []).filter((_, i) => i !== idx),
                            }))
                          }
                          className="text-rose-600 hover:text-rose-700 hover:bg-rose-50"
                          data-testid={`docs-video-remove-${idx}`}
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Visibility segment control */}
              <div data-testid="docs-visibility-section">
                <Label className="mb-2 block">Visibility</Label>
                <div className="flex gap-2 mb-3">
                  <button
                    type="button"
                    onClick={() => setVisibilityMode('all')}
                    className={`flex-1 flex items-center justify-center gap-2 rounded-md border py-2 text-sm font-medium transition-colors ${
                      visibilityMode === 'all'
                        ? 'bg-emerald-600 text-white border-emerald-600'
                        : 'bg-white text-slate-700 border-slate-200 hover:bg-slate-50'
                    }`}
                    data-testid="docs-visibility-all-btn"
                  >
                    <Unlock className="w-3.5 h-3.5" />
                    All sub-admins
                  </button>
                  <button
                    type="button"
                    onClick={() => setVisibilityMode('specific')}
                    className={`flex-1 flex items-center justify-center gap-2 rounded-md border py-2 text-sm font-medium transition-colors ${
                      visibilityMode === 'specific'
                        ? 'bg-amber-600 text-white border-amber-600'
                        : 'bg-white text-slate-700 border-slate-200 hover:bg-slate-50'
                    }`}
                    data-testid="docs-visibility-specific-btn"
                  >
                    <Lock className="w-3.5 h-3.5" />
                    Specific capabilities
                  </button>
                  <button
                    type="button"
                    onClick={() => setVisibilityMode('admin_only')}
                    className={`flex-1 flex items-center justify-center gap-2 rounded-md border py-2 text-sm font-medium transition-colors ${
                      visibilityMode === 'admin_only'
                        ? 'bg-red-600 text-white border-red-600'
                        : 'bg-white text-slate-700 border-slate-200 hover:bg-slate-50'
                    }`}
                    data-testid="docs-visibility-admin-only-btn"
                  >
                    <Lock className="w-3.5 h-3.5" />
                    Full admins only
                  </button>
                </div>

                {visibilityMode === 'all' && (
                  <div className="rounded-md bg-emerald-50 border border-emerald-200 px-3 py-2 text-xs text-emerald-800">
                    This article will be visible to <strong>all</strong> admins and sub-admins.
                  </div>
                )}

                {visibilityMode === 'specific' && (
                  <div className="space-y-2">
                    <div className="flex items-center gap-2 rounded-md bg-amber-50 border border-amber-200 px-3 py-2 text-xs text-amber-800">
                      <AlertCircle className="w-3.5 h-3.5 flex-shrink-0" />
                      Visible only to sub-admins holding <strong>any</strong> of the ticked capabilities.
                      Full admins always see all articles.
                    </div>
                    <CapabilityPicker
                      selected={form.required_capabilities}
                      onChange={(caps) => setForm((f) => ({ ...f, required_capabilities: caps }))}
                    />
                    {form.required_capabilities.length === 0 && (
                      <p className="text-xs text-red-600 font-medium" data-testid="docs-caps-warning">
                        Select at least one capability to save.
                      </p>
                    )}
                  </div>
                )}

                {visibilityMode === 'admin_only' && (
                  <div className="flex items-center gap-2 rounded-md bg-red-50 border border-red-200 px-3 py-2 text-xs text-red-800">
                    <AlertCircle className="w-3.5 h-3.5 flex-shrink-0" />
                    <span>
                      <strong>Sub-admins cannot see this article</strong>, regardless of their capabilities.
                      Only full admins have access.
                    </span>
                  </div>
                )}
              </div>

              {/* Draft / Published toggle */}
              <div className="flex items-center gap-3">
                <Switch
                  id="docs-is-active"
                  checked={form.is_active}
                  onCheckedChange={(v) => setForm({ ...form, is_active: v })}
                  data-testid="docs-is-active-switch"
                />
                <Label htmlFor="docs-is-active">
                  {form.is_active ? 'Published (visible to admins)' : 'Draft (hidden from admins)'}
                </Label>
              </div>
            </div>

            <DialogFooter>
              <Button variant="outline" onClick={() => setDialogOpen(false)}>
                Cancel
              </Button>
              <Button
                onClick={saveArticle}
                disabled={saveDisabled}
                data-testid="docs-save-btn"
              >
                <Save className="w-4 h-4 mr-2" />
                {saving ? 'Saving…' : editingArticle ? 'Update Article' : 'Create Article'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* ── Category management dialog ─────────────────────────────────── */}
        <Dialog open={catDialogOpen} onOpenChange={setCatDialogOpen}>
          <DialogContent className="max-w-md" data-testid="docs-categories-dialog">
            <DialogHeader>
              <DialogTitle>Manage Docs Categories</DialogTitle>
            </DialogHeader>
            <div className="space-y-4">
              <div className="flex gap-2">
                <Input
                  value={catNew}
                  onChange={(e) => setCatNew(e.target.value)}
                  placeholder="New category name"
                  onKeyDown={(e) => { if (e.key === 'Enter') addCategory(); }}
                  data-testid="docs-new-category-input"
                />
                <Button
                  onClick={addCategory}
                  disabled={savingCat || !catNew.trim()}
                  data-testid="docs-add-category-btn"
                >
                  <Plus className="w-4 h-4 mr-1" /> Add
                </Button>
              </div>

              {categories.length === 0 ? (
                <p className="text-sm text-slate-500 text-center py-4">
                  No categories yet. Add one above.
                </p>
              ) : (
                <DndContext
                  sensors={sensors}
                  collisionDetection={closestCenter}
                  onDragEnd={handleCategoryDragEnd}
                >
                  <SortableContext
                    items={categories.map((c) => c.id)}
                    strategy={verticalListSortingStrategy}
                  >
                    <div className="space-y-1 max-h-72 overflow-y-auto">
                      {categories.map((c) => (
                        <SortableCategoryRow key={c.id} id={c.id}>
                          <div
                            className="flex items-center justify-between p-2 border rounded-md"
                            data-testid={`docs-category-row-${c.id}`}
                          >
                            {catEditingId === c.id ? (
                              <div className="flex flex-1 gap-2">
                                <Input
                                  value={catEditName}
                                  onChange={(e) => setCatEditName(e.target.value)}
                                  onKeyDown={(e) => { if (e.key === 'Enter') saveCategoryEdit(c.id); }}
                                  autoFocus
                                />
                                <Button size="sm" onClick={() => saveCategoryEdit(c.id)} disabled={savingCat}>
                                  Save
                                </Button>
                                <Button
                                  size="sm"
                                  variant="ghost"
                                  onClick={() => { setCatEditingId(null); setCatEditName(''); }}
                                >
                                  Cancel
                                </Button>
                              </div>
                            ) : (
                              <>
                                <span className="text-sm">{c.name}</span>
                                <div className="flex items-center gap-1">
                                  <Button
                                    variant="ghost"
                                    size="sm"
                                    onClick={() => { setCatEditingId(c.id); setCatEditName(c.name); }}
                                    data-testid={`docs-edit-category-${c.id}`}
                                  >
                                    <Edit className="w-4 h-4" />
                                  </Button>
                                  <Button
                                    variant="ghost"
                                    size="sm"
                                    onClick={() => deleteCategory(c)}
                                    data-testid={`docs-delete-category-${c.id}`}
                                  >
                                    <Trash2 className="w-4 h-4 text-red-500" />
                                  </Button>
                                </div>
                              </>
                            )}
                          </div>
                        </SortableCategoryRow>
                      ))}
                    </div>
                  </SortableContext>
                </DndContext>
              )}
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setCatDialogOpen(false)}>Close</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </Layout>
  );
};

export default DocsManagerPage;
